import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface FeedbackModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (justification: string | null) => void
  isLoading?: boolean
}

// This justification is optional - `onSave` gets `null` for an empty
// draft (see onSubmit below), so unlike the other single-field forms in
// this migration there's no `.min(1)` requirement here, just a plain
// string field with no visible label/error chrome.
const feedbackSchema = z.object({
  justification: z.string(),
})
type FeedbackFormValues = z.infer<typeof feedbackSchema>

/**
 * FeedbackModal component for collecting thumbs down justification
 */
export function FeedbackModal({
  open,
  onOpenChange,
  onSave,
  isLoading = false,
}: FeedbackModalProps) {
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { justification: '' },
  })
  const { ref: justificationFieldRef, ...justificationFieldRest } = form.register('justification')

  const onSubmit = (data: FeedbackFormValues) => {
    onSave(data.justification.trim() || null)
    form.reset({ justification: '' }) // Reset on save
  }

  // form.handleSubmit(onSubmit) reads internal refs, so it's wrapped here
  // and invoked from an event handler rather than called inline during
  // render.
  const handleSave = () => {
    void form.handleSubmit(onSubmit)()
  }

  const handleCancel = () => {
    form.reset({ justification: '' })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>What should be improved?</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <textarea
            {...justificationFieldRest}
            ref={justificationFieldRef}
            placeholder="Optional: Tell us what could be better..."
            className="w-full min-h-[100px] rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all placeholder:text-textFaint focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            disabled={isLoading}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
